import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  TextField, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Chip,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  Copy, 
  Archive, 
  Filter,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../Config/config';
import ProjectFormSections, { DEFAULT_PROJECT_FORM } from './ProjectFormSections';
import ProjectWizard from './projectWizard/ProjectWizard';

const ProjectsList = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [techStacks, setTechStacks] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const emptyForm = DEFAULT_PROJECT_FORM;
  const [formData, setFormData] = useState(emptyForm);
  const handleFormChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    fetchProjects();
    fetchTechStacks();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      
      // Perform a health check first to verify service availability
      try {
        const healthRes = await axios.get(`${API_BASE_URL}/api/nextgen-builder/health`);
        console.log('NextGen Builder Health Check:', healthRes.data);
      } catch (hErr) {
        console.warn('Health check failed, service might be down or route not deployed:', hErr.message);
      }

      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/projects`);
      if (res.data.success) {
        setProjects(res.data.data);
      } else {
        setSnackbar({ open: true, message: res.data.message || 'Failed to fetch projects', severity: 'error' });
      }
    } catch (err) {
      console.error('Failed to fetch projects', err);
      setSnackbar({ open: true, message: 'Failed to load projects. API might be unavailable.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchTechStacks = async () => {
    try {
      console.log(`Fetching tech stacks from: ${API_BASE_URL}/api/nextgen-builder/tech-stacks`);
      // Use a timestamp to avoid any potential caching issues
      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/tech-stacks?t=${Date.now()}`);
      console.log('Tech stacks response:', res.data);
      if (res.data.success) {
        setTechStacks(res.data.data);
      } else {
        console.error('Tech stacks fetch success=false:', res.data);
      }
    } catch (err) {
      console.error('Failed to fetch tech stacks', err);
      if (err.response) {
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
      }
      setSnackbar({ open: true, message: 'Failed to load tech stacks. API might be unavailable.', severity: 'error' });
    }
  };

  const handleAddProject = async () => {
    if (!formData.name) {
      setSnackbar({ open: true, message: 'Project name is required', severity: 'warning' });
      return;
    }
    if (!formData.tech_stacks.length && !formData.tech_stack_id) {
      setSnackbar({ open: true, message: 'Please select at least one Tech Stack', severity: 'warning' });
      return;
    }
    try {
      // If a selected stack matches a registered tech stack, link it (drives team templates)
      const matchedStack = techStacks.find(s =>
        formData.tech_stacks.some(sel => sel.toLowerCase().includes(String(s.name).toLowerCase()) || String(s.name).toLowerCase().includes(sel.toLowerCase()))
      );
      const payload = {
        ...formData,
        tech_stack_id: formData.tech_stack_id || matchedStack?.id || null,
        primary_language: formData.languages.join(', ') || formData.primary_language,
        tags: typeof formData.tags === 'string'
          ? formData.tags.split(',').map(t => t.trim()).filter(Boolean)
          : formData.tags
      };
      const res = await axios.post(`${API_BASE_URL}/api/nextgen-builder/projects`, payload);
      if (res.data.success) {
        setOpenAddDialog(false);
        fetchProjects();
        setFormData(emptyForm);
        setSnackbar({ open: true, message: 'Project created successfully', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: res.data.message || 'Failed to add project', severity: 'error' });
      }
    } catch (err) {
      console.error('Failed to add project', err);
      let errorMsg = 'Error occurred while creating project';
      if (err.response?.data) {
        errorMsg = err.response.data.message || err.response.data.error || errorMsg;
        if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          errorMsg += ': ' + err.response.data.errors.join(', ');
        }
      }
      setSnackbar({ 
        open: true, 
        message: errorMsg, 
        severity: 'error' 
      });
    }
  };

  const handleClone = async (id) => {
    if (window.confirm('Are you sure you want to clone this project?')) {
      try {
        const res = await axios.post(`${API_BASE_URL}/api/nextgen-builder/projects/${id}/clone`);
        if (res.data.success) {
          setSnackbar({ open: true, message: 'Project cloned successfully', severity: 'success' });
          fetchProjects();
        } else {
          setSnackbar({ open: true, message: res.data.message || 'Clone failed', severity: 'error' });
        }
      } catch (err) {
        console.error('Clone failed', err);
        setSnackbar({ open: true, message: 'Error cloning project', severity: 'error' });
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        const res = await axios.delete(`${API_BASE_URL}/api/nextgen-builder/projects/${id}`);
        if (res.data.success) {
          setSnackbar({ open: true, message: 'Project deleted successfully', severity: 'success' });
          fetchProjects();
        } else {
          setSnackbar({ open: true, message: res.data.message || 'Delete failed', severity: 'error' });
        }
      } catch (err) {
        console.error('Delete failed', err);
        setSnackbar({ open: true, message: 'Error deleting project', severity: 'error' });
      }
    }
  };

  const handleArchive = async (id) => {
    if (window.confirm('Are you sure you want to archive this project?')) {
      try {
        const res = await axios.post(`${API_BASE_URL}/api/nextgen-builder/projects/${id}/archive`);
        if (res.data.success) {
          setSnackbar({ open: true, message: 'Project archived successfully', severity: 'success' });
          fetchProjects();
        } else {
          setSnackbar({ open: true, message: res.data.message || 'Archive failed', severity: 'error' });
        }
      } catch (err) {
        console.error('Archive failed', err);
        setSnackbar({ open: true, message: 'Error archiving project', severity: 'error' });
      }
    }
  };

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.tech_stack_name && p.tech_stack_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Box p={4} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton 
            onClick={() => navigate('/dashboard')} 
            sx={{ 
              color: '#843FEF', 
              bgcolor: 'white', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              '&:hover': { bgcolor: '#f0eaff' }
            }}
          >
            <ArrowLeft size={24} />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="800" sx={{ color: '#1e293b', letterSpacing: '-0.02em' }}>
              NextGen Builder
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage projects, versions, and engineering teams
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1.5}>
          <Button
            variant="outlined"
            onClick={() => navigate('/nextgen/github-access')}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1.2,
              textTransform: 'none',
              fontWeight: 'bold',
              borderColor: '#843FEF',
              color: '#843FEF',
              '&:hover': { borderColor: '#7035cc', bgcolor: '#f0eaff' }
            }}
          >
            GitHub Access
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/nextgen/finance')}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1.2,
              textTransform: 'none',
              fontWeight: 'bold',
              borderColor: '#843FEF',
              color: '#843FEF',
              '&:hover': { borderColor: '#7035cc', bgcolor: '#f0eaff' }
            }}
          >
            Finance
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => setOpenAddDialog(true)}
            sx={{
              bgcolor: '#843FEF',
              '&:hover': { bgcolor: '#7035cc' },
              borderRadius: '12px',
              px: 4,
              py: 1.2,
              textTransform: 'none',
              fontWeight: 'bold',
              boxShadow: '0 4px 14px 0 rgba(132, 63, 239, 0.39)'
            }}
          >
            New Project
          </Button>
        </Box>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          p: 2.5, 
          mb: 4, 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.01), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}
      >
        <TextField 
          placeholder="Search projects by name or technology..." 
          size="small" 
          fullWidth
          InputProps={{ 
            startAdornment: <Search size={18} style={{ marginRight: 12, color: '#94a3b8' }} />,
            sx: { borderRadius: '10px', bgcolor: '#f1f5f9', border: 'none', '& fieldset': { border: 'none' } }
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Button 
          variant="outlined" 
          startIcon={<Filter size={18} />} 
          sx={{ 
            minWidth: 120, 
            borderRadius: '10px', 
            textTransform: 'none', 
            borderColor: '#e2e8f0',
            color: '#64748b',
            '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
          }}
        >
          Filters
        </Button>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={10}>
          <CircularProgress sx={{ color: '#843FEF' }} />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: '600', color: '#64748b', py: 2.5 }}>PROJECT NAME</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>TECH STACK</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>LANGUAGE</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>ARCHITECTURE</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>DIFFICULTY</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }} align="center">VERSIONS</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>STATUS</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }} align="right">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProjects.map((project) => (
                <TableRow 
                  key={project.id} 
                  hover 
                  onClick={() => navigate(`/nextgen/projects/${project.id}`)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f1f5f9 !important' },
                    '& td': { py: 2, borderBottom: '1px solid #f1f5f9' }
                  }}
                >
                  <TableCell>
                    <Typography fontWeight="700" color="#1e293b">{project.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {(project.tech_stacks?.length ? project.tech_stacks : [project.tech_stack_name].filter(Boolean)).slice(0, 3).map((ts, i) => (
                        <Chip
                          key={i}
                          label={ts}
                          size="small"
                          sx={{
                            bgcolor: '#eef2ff',
                            color: '#4f46e5',
                            fontWeight: '600',
                            borderRadius: '6px'
                          }}
                        />
                      ))}
                      {(project.tech_stacks?.length || 0) > 3 && (
                        <Chip label={`+${project.tech_stacks.length - 3}`} size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', borderRadius: '6px' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell color="#475569">{project.languages?.length ? project.languages.join(', ') : project.primary_language}</TableCell>
                  <TableCell color="#475569">{project.architecture}</TableCell>
                  <TableCell>
                    {project.difficulty_level ? (
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Chip
                          label={project.difficulty_level}
                          size="small"
                          sx={{ bgcolor: '#f5f3ff', color: '#6d28d9', fontWeight: '600', borderRadius: '6px', width: 'fit-content' }}
                        />
                        {project.estimated_duration && (
                          <Typography variant="caption" color="#94a3b8">
                            {project.estimated_duration} {project.duration_unit || 'Days'}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="#cbd5e1">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 'bold', fontSize: '0.75rem' }}>
                      {project.total_versions}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={project.status} 
                      size="small" 
                      variant="soft"
                      sx={{ 
                        fontWeight: '600',
                        textTransform: 'capitalize',
                        bgcolor: project.status === 'active' ? '#dcfce7' : '#f1f5f9',
                        color: project.status === 'active' ? '#166534' : '#64748b',
                        borderRadius: '6px'
                      }} 
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" gap={0.5}>
                      <Tooltip title="View">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/nextgen/projects/${project.id}`); }} sx={{ color: '#64748b' }}>
                          <Eye size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clone">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleClone(project.id); }} sx={{ color: '#64748b' }}>
                          <Copy size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Archive">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleArchive(project.id); }} sx={{ color: '#64748b' }}>
                          <Archive size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}>
                          <Trash2 size={18} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {filteredProjects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 12 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <Typography variant="h6" color="#94a3b8" fontWeight="600">No projects found</Typography>
                      <Typography variant="body2" color="#cbd5e1">Try adjusting your search or add a new project</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Project — the same wizard Edit uses, in create mode. The old
          single-scroll "Add New Project" dialog is gone: one component now
          serves both flows, so the two screens cannot drift apart again.
          Mounted only while open so each run starts from clean state. */}
      {openAddDialog ? (
        <ProjectWizard
          open={openAddDialog}
          mode="create"
          techStacks={techStacks}
          onClose={() => setOpenAddDialog(false)}
          onSaved={() => {
            setOpenAddDialog(false);
            fetchProjects();
            setSnackbar({ open: true, message: 'Project created successfully', severity: 'success' });
          }}
        />
      ) : null}

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectsList;
